import { useState } from 'react';
import { formKaydiBulguKaynagiMi } from '@isg/validation';
import { Link, useParams } from 'react-router';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { AssignableUser, FormSubmissionDetail, MyAuthorization } from '@isg/shared-types';
import {
  useAtanabilirler,
  useCiktiIndir,
  useFormEkIndir,
  useFormEkSil,
  useFormEkYukle,
  useFormGonder,
  useFormKayit,
  useFormKayittanAksiyon,
  useLookup,
} from '../api/queries';
import { SatirEylemleri } from '../sozlesme';
import { EkBolumu, KayitGorunumu, semayiCoz } from './form-parts';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * DOLU FORM KAYDI SAYFASI (Tur 3 — F4 + F7).
 *
 * ⚠️ **P5 — SEKME YOK, ÇIKTIYA GİDEN ŞEY SAYFANIN KENDİSİDİR:** künye ·
 * cevaplar · kontrol özeti · ekler alt alta durur ve PDF aynı sırayı basar.
 *
 * ⚠️ **F2 — DÜĞMELER SUNUCUDAN.** *"Aksiyon aç"* eskiden ekrandaki bir şapka
 * listesinden türetiliyordu; o liste sunucununkinden AYRIŞMIŞTI (teknisyen
 * eksikti) ve `uzman_aksiyon_atama` ayarını hiç okumuyordu — ayarı kapalı
 * holdingde düğme görünüyor, basılınca 403 veriyordu (§2.40 — yalan söyleyen
 * kontrol). Artık tek kaynak `yetkiler` alanıdır.
 */
export function FormKayitScreen({ authz }: { authz: MyAuthorization }): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const kayit = useFormKayit(id ?? null);

  return (
    <Stack data-testid="form-kayit-sayfasi">
      <Breadcrumbs data-testid="form-kayit-gezinti">
        <Link to="/formlar">Formlar</Link>
        <Text>{kayit.data?.formTemplateAd ?? 'Form kaydı'}</Text>
      </Breadcrumbs>
      <QueryStatus queries={[kayit]} kayit />
      {kayit.isSuccess && (
        <KayitGovdesi detay={kayit.data} authz={authz} onTazele={() => void kayit.refetch()} />
      )}
    </Stack>
  );
}

function KayitGovdesi({
  detay,
  authz,
  onTazele,
}: {
  detay: FormSubmissionDetail;
  authz: MyAuthorization;
  onTazele: () => void;
}): React.JSX.Element {
  const lookup = useLookup('olay_turu');
  const gonderMut = useFormGonder();
  const ekYukle = useFormEkYukle();
  const ekSil = useFormEkSil();
  const ekIndir = useFormEkIndir();
  const ciktiIndir = useCiktiIndir();
  const [aksiyonAcik, setAksiyonAcik] = useState(false);

  const sema = semayiCoz(detay.sema);
  const taslak = detay.durum === 'draft';
  const kendiKaydi = detay.dolduranAppUserId === authz.appUserId;
  const ozet = detay.kontrolOzeti;

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Group gap="xs" align="baseline">
          <Title order={3} data-testid="form-kayit-baslik">
            {detay.formTemplateAd}
          </Title>
          <Badge size="sm" variant="light" data-testid="form-kayit-surum">
            v{detay.surumNo}
          </Badge>
          <Badge size="sm" color={taslak ? ANLAM.dikkat : ANLAM.olumlu} variant="light">
            {taslak ? 'Taslak' : 'Gönderildi'}
          </Badge>
        </Group>
        <Group gap="xs">
          {/* P2: birincil eylem kullanıcıya göre — taslakta "Gönder",
              gönderilmişte çıktı (yazamayanın tek yapabildiği şey). */}
          {taslak && detay.yetkiler.gonderebilir && kendiKaydi ? (
            <Button
              data-testid="form-kayit-gonder"
              loading={gonderMut.isPending}
              onClick={() => gonderMut.mutate({ id: detay.id }, { onSuccess: onTazele })}
            >
              Gönder
            </Button>
          ) : (
            !taslak && (
              <Button
                data-testid="form-kayit-pdf"
                loading={ciktiIndir.isPending}
                onClick={() =>
                  ciktiIndir.mutate({
                    yol: `/api/form-submissions/${detay.id}/pdf`,
                    yedekAd: `${detay.formTemplateAd}.pdf`,
                  })
                }
              >
                PDF indir
              </Button>
            )
          )}
          <SatirEylemleri
            testId="form-kayit-eylemler"
            /* ⚠️ PDF menüde TEKRARLANMAZ: gönderilmiş kayıtta zaten BİRİNCİL
               eylemdir (P2 — satırda/başlıkta tek birincil). Menüde yalnız
               kayıt eylemleri durur; hepsi gizliyse menü HİÇ çizilmez ve bu
               doğrudur — boş bir ⋮ bir yalandır. */
            digerleri={[
              {
                etiket: 'Aksiyon aç',
                testId: 'form-kayittan-aksiyon',
                // F2 — TEK KAYNAK SUNUCU: şapka listesi + tenant ayarı + bahçe
                // orada çözülür; ekran kural türetmez.
                gizli: !detay.yetkiler.aksiyonAcabilir,
                onClick: () => setAksiyonAcik(true),
              },
            ]}
          />
        </Group>
      </Group>

      <Card withBorder data-testid="form-kayit-kunye">
        <Group gap="xl" wrap="wrap">
          <Kunye etiket="İşyeri" deger={detay.workplaceAd} />
          <Kunye etiket="İş ekipmanı" deger={detay.ekipmanAd ?? '—'} />
          <Kunye etiket="Tarih" deger={tarihGoster(detay.doldurmaTarihi)} />
          <Kunye etiket="Dolduran" deger={detay.dolduranEtiket} />
          {detay.ptwPermitId && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                İş izni
              </Text>
              <Link to={`/izinler/${detay.ptwPermitId}`} data-testid="form-kayit-izin-baglantisi">
                İzin kartına git
              </Link>
            </Stack>
          )}
          {/* Saha turu / S2 — KAP: bu kayıt bir denetim oturumunun formudur;
              uygunsuz maddeleri tur kapanışında bulgu adayıdır. Kap yoksa
              hiçbir şey doğmaz (U10) — o yüzden satır YALNIZ kapta çizilir. */}
          {formKaydiBulguKaynagiMi(detay) && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Saha turu
              </Text>
              <Link
                to={`/saha-turlari/${detay.siteTourId}`}
                data-testid="form-kayit-saha-turu-baglantisi"
              >
                Denetim turuna git
              </Link>
            </Stack>
          )}
        </Group>
      </Card>

      {!taslak && (
        <Text size="xs" c="dimmed" data-testid="form-surum-notu">
          Bu kayıt doldurulduğu günkü şablon sürümüyle gösteriliyor; sonradan yapılan şablon
          değişiklikleri kaydı etkilemez.
        </Text>
      )}

      {/* Ş4/Ş5 — KONTROL ÖZETİ: sayım GÖRÜNÜR, hiçbir tetik ÜRETMEZ. Uygunsuz
          maddeye DÖF gerekiyorsa onu insan açar ("Aksiyon aç"). */}
      {ozet.toplam > 0 && (
        <Card withBorder data-testid="form-kontrol-ozeti">
          <Group gap="xl" wrap="wrap">
            <Kunye etiket="Kontrol maddesi" deger={String(ozet.toplam)} />
            <Kunye etiket="Olumlu" deger={String(ozet.olumlu)} />
            <Kunye etiket="Olumsuz" deger={String(ozet.olumsuz)} />
            <Kunye etiket="Kapsam dışı" deger={String(ozet.kapsamDisi)} />
            <Kunye etiket="Cevapsız" deger={String(ozet.cevapsiz)} />
          </Group>
          {ozet.uygunsuzlar.length > 0 && (
            <Table mt="sm" withTableBorder data-testid="form-uygunsuz-tablosu">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Uygunsuz madde</Table.Th>
                  <Table.Th w={120}>Cevap</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ozet.uygunsuzlar.map((satir) => (
                  <Table.Tr key={satir.alanKodu} data-testid={`form-uygunsuz-${satir.alanKodu}`}>
                    <Table.Td>{satir.etiket}</Table.Td>
                    <Table.Td>{satir.cevapEtiketi}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      )}

      {sema ? (
        <>
          <KayitGorunumu
            sema={sema}
            cevaplar={detay.cevaplar}
            lookupKalemleri={lookup.data ?? []}
            /* G1 — madde notu + kanıt fotoğrafı cevabın ALTINDA görünür;
               ekler zaten kayıtla birlikte geliyordu, eksik olan gösterimdi. */
            ekler={detay.ekler}
            onOnizleme={async (attachmentId) => (await ekIndir(detay.id, attachmentId)).blob}
          />
          <EkBolumu
            sema={sema}
            ekler={detay.ekler}
            duzenlenebilir={taslak && kendiKaydi}
            onYukle={(alanKodu, dosya) =>
              ekYukle.mutate({ submissionId: detay.id, alanKodu, dosya }, { onSuccess: onTazele })
            }
            onSil={(attachmentId) =>
              ekSil.mutate({ submissionId: detay.id, attachmentId }, { onSuccess: onTazele })
            }
            onIndir={(ek) => {
              void ekIndir(detay.id, ek.id).then(({ blob, dosyaAdi }) => {
                // Baytlar API'den geldi; geçici object URL yalnız kaydetme
                // için — public URL/SAS YOK (Kapsam §22.4/6).
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = dosyaAdi ?? ek.dosyaAdi;
                link.click();
                URL.revokeObjectURL(url);
              });
            }}
          />
        </>
      ) : (
        <Alert color={ANLAM.engel} role="alert">
          Kaydın şablon sürümü okunamadı.
        </Alert>
      )}

      {(ekYukle.isError || ekSil.isError || gonderMut.isError || ciktiIndir.isError) && (
        <Alert color={ANLAM.engel} role="alert" data-testid="form-kayit-hata">
          {(ekYukle.error ?? ekSil.error ?? gonderMut.error ?? ciktiIndir.error)?.message}
        </Alert>
      )}
      {!taslak && detay.acilanAksiyonSayisi > 0 && (
        <Text size="sm" data-testid="form-kayit-baglar">
          Bu kayıttan açılan: {detay.acilanAksiyonSayisi} aksiyon
        </Text>
      )}
      {taslak && detay.ptwPermitId !== null && (
        <Text size="xs" c="dimmed" data-testid="form-kayit-izin-notu">
          Bu kayıt bir iş iznine bağlıdır; doldurma ve gönderim izin kartındadır (S8).
        </Text>
      )}
      {aksiyonAcik && (
        <AksiyonFormu
          kayitId={detay.id}
          onKapat={() => {
            setAksiyonAcik(false);
            onTazele();
          }}
        />
      )}
    </Stack>
  );
}

function Kunye({ etiket, deger }: { etiket: string; deger: string }): React.JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm">{deger}</Text>
    </Stack>
  );
}

/** Kayıttan aksiyon: kaynak + işyeri SUNUCUDA kayıttan türetilir (§22.5/7). */
function AksiyonFormu({
  kayitId,
  onKapat,
}: {
  kayitId: string;
  onKapat: () => void;
}): React.JSX.Element {
  const atanabilirler = useAtanabilirler();
  const ac = useFormKayittanAksiyon();
  const [baslik, setBaslik] = useState('');
  const [atanan, setAtanan] = useState<string | null>(null);
  const [termin, setTermin] = useState('');

  return (
    <Modal opened onClose={onKapat} title="Kayıttan aksiyon aç" size="md">
      <Stack>
        <Text size="xs" c="dimmed">
          Kaynak ve işyeri form kaydından otomatik bağlanır.
        </Text>
        <TextInput
          label="Başlık"
          required
          value={baslik}
          onChange={(event) => setBaslik(event.currentTarget.value)}
          data-testid="form-aksiyon-baslik"
        />
        <AtananSecici atanabilirler={atanabilirler.data ?? []} deger={atanan} onDeger={setAtanan} />
        <TextInput
          label="Termin"
          type="date"
          required
          value={termin}
          onChange={(event) => setTermin(event.currentTarget.value)}
          data-testid="form-aksiyon-termin"
        />
        {ac.isError && (
          <Alert color={ANLAM.engel} role="alert" data-testid="form-aksiyon-hata">
            {ac.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            disabled={!baslik.trim() || !atanan || !termin || ac.isPending}
            data-testid="form-aksiyon-kaydet"
            onClick={() =>
              ac.mutate(
                {
                  id: kayitId,
                  girdi: {
                    baslik: baslik.trim(),
                    atananAppUserId: atanan!,
                    terminTarihi: termin,
                  },
                },
                { onSuccess: onKapat },
              )
            }
          >
            Aksiyonu aç
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function AtananSecici({
  atanabilirler,
  deger,
  onDeger,
}: {
  atanabilirler: AssignableUser[];
  deger: string | null;
  onDeger: (deger: string | null) => void;
}): React.JSX.Element {
  return (
    <Select
      label="Atanan"
      required
      searchable
      data={atanabilirler.map((kisi) => ({ value: kisi.appUserId, label: kisi.etiket }))}
      value={deger}
      onChange={onDeger}
      data-testid="form-aksiyon-atanan"
    />
  );
}
